/**
 * A list of `AccountTalkRate` rows as other areas' answers carry them (plan-017 READ-B, OQ-1 "show the relevant rate"):
 * `VoiceAgentDetail.talk_rates` (the rates of a VoiceAgent's connections), `WebChatInstall.talk_rates` (a website
 * widget's `text`, and `browser` when it takes voice) and `CommercialOfferPrice.talk_rates` (the organization's six).
 * Written by plan-017 unit WAPP-B beside the `talk-rate` slice's own reader (`./slice/talk-rate.ts`, WAPP-A), into the
 * same `TalkRateView`, so every screen words a rate through `talkRateText` and `talkRateKindLabel`.
 *
 * EVERY FIGURE IS THE API'S: a kind with no price is absent from the list and absent here — never defaulted — and the
 * hourly figure is the server's (`talk_rate_minor_per_hour`), never multiplied in the browser.
 */
import { CURRENCY_CODE, money, TALK_RATE_SOURCE } from "@wetalk/domain";

import { readTalkRateKind } from "./conversation-view.js";
import { MalformedResponseError, readArray, readRecord, readText, readWholeNumber } from "./decode.js";
import type { TalkRateView } from "./slice/talk-rate.js";

/** One `AccountTalkRate` row. */
export function accountTalkRateOfWire(value: unknown, path: string): TalkRateView {
  const row = readRecord(value, path);
  const currency = readText(row["currency"], `${path}.currency`);
  if (currency !== CURRENCY_CODE) throw new MalformedResponseError(`${path}.currency`, CURRENCY_CODE, currency);
  const raw = readText(row["source"], `${path}.source`);
  // plan-020 PIN (design §32.2.4): `offer` — the plan's rate — joins `standard` and `account`; anything else is refused.
  const source = TALK_RATE_SOURCE.find((one) => one === raw);
  if (source === undefined) throw new MalformedResponseError(`${path}.source`, TALK_RATE_SOURCE.join(", "), raw);
  return {
    kind: readTalkRateKind(row["talk_rate_kind"], `${path}.talk_rate_kind`),
    perMinute: money(readWholeNumber(row["talk_rate_minor_per_minute"], `${path}.talk_rate_minor_per_minute`), CURRENCY_CODE),
    perHour: money(readWholeNumber(row["talk_rate_minor_per_hour"], `${path}.talk_rate_minor_per_hour`), CURRENCY_CODE),
    source,
  };
}

/** A `talk_rates` array, in the order the API gives (`TalkRateKind`'s). */
export function accountTalkRatesOfWire(value: unknown, path: string): readonly TalkRateView[] {
  return readArray(value, path).map((one, index) => accountTalkRateOfWire(one, `${path}[${String(index)}]`));
}
